import type { ConditionType } from './condition-type.constants';

export const QUICK_PICK_DIAGNOSES_LIMIT = 12;

/** Below this many answered cases of a condition type, the fixed list shows instead of the ranking. */
export const MOST_USED_MIN_ANSWERED_CASES = 100;

const FIXED_QUICK_PICK_IDENTIFIERS: Partial<
  Record<ConditionType, readonly string[]>
> = {
  Lesion: [
    'unspecified_nevus',
    'seborrheic_keratosis',
    'dermatofibroma',
    'actinic_keratosis',
    'squamous_cell_carcinoma',
    'basal_cell_carcinoma',
    'hemangioma',
    'unspecified_melanoma',
    'other_benign',
    'dermal_nevus',
  ],
  Rash: [
    'dermatitis',
    'eczema',
    'psoriasis',
    'atopic_dermatitis',
    'allergic_contact_dermatitis',
    'seborrheic_dermatitis',
    'tinea_versicolor',
  ],
};

interface QuickPickCandidate {
  id: string;
  legacyIdentifier?: string;
}

/** The hand-picked list; a condition type without one gets none. */
export function fixedQuickPicks<T extends QuickPickCandidate>(
  diagnoses: readonly T[],
  conditionType: string | undefined,
): T[] {
  const identifiers =
    FIXED_QUICK_PICK_IDENTIFIERS[conditionType as ConditionType] ?? [];
  return diagnoses.filter(
    ({ legacyIdentifier }) =>
      !!legacyIdentifier && identifiers.includes(legacyIdentifier),
  );
}

/** The ranking leads and the fixed list fills the remaining slots. */
export function mostUsedThenFixed<T extends QuickPickCandidate>(
  mostUsed: readonly T[],
  fixed: readonly T[],
): T[] {
  // A repeat would otherwise eat a slot and hide a distinct diagnosis.
  const seen = new Set<string>();
  return [...mostUsed, ...fixed]
    .filter(({ id }) => {
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .slice(0, QUICK_PICK_DIAGNOSES_LIMIT);
}
